// ── Los recorridos guiados, por módulo ───────────────────────────────────────
//
// Viven acá y no en i18n suelto porque envejecen igual que un artículo de ayuda
// y conviene revisarlos juntos: los dos describen pantallas que cambian.
//
// Un paso apunta a un elemento por `ancla` (su `data-tour-id`). Nunca por
// selector de CSS: `.flex > div:nth-child(2)` es una bomba de tiempo con cada
// refactor. Ver DESIGN_SYSTEM.md → Spotlight Pattern.
//
// ── Un recorrido describe la PANTALLA POR LA QUE SE ENTRA al módulo ──────────
//
// El de `funnels` describía el tablero (/embudos/:id) y estaba montado ahí,
// pero al módulo se entra por la lista (/embudos). Resultado, medido en
// producción el 2026-09-11: no salía nunca, y menos todavía en una cuenta sin
// embudos, que es exactamente a quien está dirigido. Los pasos describen ahora
// la lista. Si el tablero necesita el suyo, es otro recorrido.

import type { IdiomaAyuda } from './index.js';

/** Un módulo que tiene recorrido. No todos lo tienen. */
export type ModuloConRecorrido =
  | 'funnels' | 'contacts'
  | 'email' | 'leads' | 'projects' | 'forms'
  | 'reports' | 'billing' | 'messaging' | 'tasks';

export interface PasoDeRecorrido {
  /** El `data-tour-id` del elemento a resaltar. */
  ancla: string;
  titulo: string;
  texto: string;
  /**
   * Ancho mínimo de ventana para que el paso tenga sentido.
   *
   * El menú lateral no existe en 390px: señalarlo ahí es señalar el aire, así
   * que el paso se salta. Sin valor, el paso va en cualquier ancho.
   */
  anchoMin?: number;
  /**
   * Cuándo tiene sentido este paso según lo que haya en pantalla.
   *
   * Decisión de Eduardo (2026-09-11): con el módulo vacío el recorrido corre
   * igual, pero mostrando dónde se crea la primera cosa — un módulo vacío es
   * justamente cuando alguien necesita que le digan por dónde empezar. Así que
   * un paso puede existir solo para el caso vacío y otro solo para el caso con
   * datos, en vez de un texto tibio que sirva para los dos.
   */
  cuando?: 'vacio' | 'con-datos';
}

type Recorridos = Record<ModuloConRecorrido, PasoDeRecorrido[]>;

const ES: Recorridos = {
  reports: [
    {
      ancla: 'reports-nuevo',
      titulo: 'Dos tipos de reporte',
      texto: 'La auditoría web mide un sitio con Google PageSpeed —velocidad, SEO y accesibilidad— y la consulta interna publica como documento el resultado de una pregunta a tu propia base.',
    },
    {
      ancla: 'reports-estado',
      titulo: 'Un reporte no está listo al instante',
      texto: 'Pendiente, Generando, Listo o Falló. Lo arma el worker en segundo plano: puedes cerrar la pantalla y seguir en otra cosa, aparece acá cuando termina.',
      cuando: 'con-datos',
    },
    {
      ancla: 'reports-fila',
      titulo: 'Se comparte con un enlace',
      texto: 'Un reporte listo se le manda al cliente con un enlace que abre sin cuenta y sin instalar nada. Es lo que hace que sirva para mostrar trabajo.',
      cuando: 'con-datos',
    },
    {
      ancla: 'reports-tipo',
      titulo: 'Se acumulan rápido',
      texto: 'Cada medición de cada sitio queda guardada. Filtrar por tipo y por estado es lo que mantiene la lista usable después del primer mes.',
      cuando: 'con-datos',
      anchoMin: 900,
    },
    {
      ancla: 'reports-lista',
      titulo: 'Para qué sirve el primero',
      texto: 'Una auditoría web sobre el sitio de un cliente te da la foto de cómo está hoy: qué anda lento, qué le falta para Google y qué barreras tiene. Es con lo que se justifica el trabajo que viene después.',
      cuando: 'vacio',
    },
  ],
  billing: [
    {
      ancla: 'billing-vista',
      titulo: 'Tres cosas distintas en un lugar',
      texto: 'Una cotización es lo que propones; una factura es lo que cobras; un recurrente es un cobro automático con tarjeta: el cliente la carga una vez y se le cobra todos los meses solo, sin que emitas nada.',
    },
    {
      ancla: 'billing-nuevo',
      titulo: 'El contacto es obligatorio, el proyecto no',
      texto: 'Sin contacto no hay documento. Sin proyecto sí: la cotización existe igual y aparece en la ficha del contacto y en esta lista — solo no sale en la pestaña de facturación de ningún proyecto.',
    },
    {
      ancla: 'billing-fila',
      titulo: 'De cotización a factura sin rehacerla',
      texto: 'Lo que el cliente aprueba se convierte en factura con las mismas líneas. Y los conceptos que usas siempre se guardan una vez en Ajustes → Datos y se traen con el buscador.',
      cuando: 'con-datos',
    },
    {
      ancla: 'billing-buscar',
      titulo: 'Buscar por folio o por cliente',
      texto: 'El folio es el número del documento. Sirve para cuando el cliente te escribe citándolo y no te acuerdas de qué era.',
      cuando: 'con-datos',
      anchoMin: 900,
    },
    {
      ancla: 'billing-lista',
      titulo: 'Mandarlo siempre pide tu confirmación',
      texto: 'Evo Brain puede armarte el documento entero con sus líneas y su precio, pero mandárselo al cliente frena y espera que le digas que sí. Es dinero, y un documento enviado no se deshace.',
      cuando: 'vacio',
    },
  ],
  messaging: [
    {
      ancla: 'messaging-canal',
      titulo: 'Todos tus canales en una bandeja',
      texto: 'Instagram, Messenger y WhatsApp en la misma lista, sin entrar a cada app. Se conectan una vez en Ajustes → Canales y desde ahí entran solos.',
    },
    {
      ancla: 'messaging-estado',
      titulo: '«Por vencer» es el que hay que mirar',
      texto: 'Meta da 24 horas para responder, contadas desde el último mensaje de la persona. Pasadas, no se le puede escribir hasta que vuelva a escribir ella. Por eso vencer una conversación cuesta caro.',
      cuando: 'con-datos',
    },
    {
      ancla: 'messaging-fila',
      titulo: 'Respondes desde acá, llega como siempre',
      texto: 'La persona recibe tu respuesta en la misma conversación de Instagram o WhatsApp donde te escribió. No se entera de que contestaste desde otro lado.',
      cuando: 'con-datos',
    },
    {
      ancla: 'messaging-estado',
      titulo: 'Archivar no es borrar',
      texto: 'Saca la conversación de la lista y conserva el historial. Si la persona vuelve a escribir, reaparece con todo lo anterior.',
      cuando: 'con-datos',
    },
    {
      ancla: 'messaging-lista',
      titulo: 'Primero hay que conectar un canal',
      texto: 'Los mensajes de Instagram, Messenger y WhatsApp entran acá una vez que conectas la cuenta en Ajustes → Canales. Hasta entonces esta lista va a estar vacía aunque te escriban.',
      cuando: 'vacio',
    },
  ],
  tasks: [
    {
      ancla: 'tasks-lista',
      titulo: 'Acá cae todo lo que lleva tiempo',
      texto: 'Lo que le encargas a Evo Brain, los análisis, las páginas que se generan y los reportes. No se hacen mientras esperas: quedan como tarea y se ejecutan en segundo plano.',
    },
    {
      ancla: 'tasks-estado',
      titulo: 'Dos estados que conviene entender',
      texto: '«Bloqueada» quiere decir que falta algo para poder seguir —una clave, un dato, una decisión tuya—. «Fallida» es que se intentó y salió mal. Los otros cinco se explican solos.',
      cuando: 'con-datos',
    },
    {
      ancla: 'tasks-fila',
      titulo: 'Una tarea cuenta qué hizo',
      texto: 'Al abrirla ves lo que efectivamente pasó, no solo que terminó. Y si falló, por qué falló — que es lo que se mira cuando algo salió distinto de lo esperado.',
      cuando: 'con-datos',
    },
    {
      ancla: 'tasks-lista',
      titulo: 'Quién decide si Evo Brain ejecuta solo',
      texto: 'En Ajustes → Automatización hay un interruptor entre «propone y espera» y «ejecuta solo». Con el primero, una tarea no arranca hasta que la apruebas acá. No hace falta tocarlo el primer día, pero conviene saber que existe.',
    },
    {
      ancla: 'tasks-nuevo',
      titulo: 'También puedes encargar una tú',
      texto: 'Sin pedírselo por chat: se crea la tarea, queda en la lista y se ejecuta igual que las que encola Evo Brain.',
      cuando: 'con-datos',
    },
  ],
  email: [
    {
      ancla: 'email-vistas',
      titulo: 'Tu bandeja tiene siete vistas',
      texto: 'Todo, Sin leer, Seguimiento, Enviados, Borradores, Categorías y Papelera. La que no se adivina es Seguimiento: junta lo que quedó esperando una respuesta tuya, con el número al lado.',
    },
    {
      ancla: 'email-categorias',
      titulo: 'El correo llega clasificado',
      texto: 'Cada mensaje cae en una categoría al entrar. Si no encuentras uno, revisa acá antes de darlo por perdido: el contador del menú no cuenta lo que está en una categoría oculta.',
    },
    {
      ancla: 'email-modo',
      titulo: 'Mensaje suelto o conversación',
      texto: 'Hilos agrupa todo lo que fue y vino con la misma persona sobre el mismo tema. Mensajes los muestra uno por uno, como llegaron.',
      anchoMin: 900,
    },
    {
      ancla: 'email-fila',
      titulo: 'Responder sale de tu casilla',
      texto: 'Evo Hub no es una dirección nueva: lee de tu casilla de siempre y responde desde ella. Quien reciba tu respuesta ve tu dirección, no una de la plataforma.',
      cuando: 'con-datos',
    },
    {
      ancla: 'email-lista',
      titulo: 'Si está vacía, revisa la casilla',
      texto: 'Evo Hub no recibe correo: lo lee de tu casilla cada pocos minutos. Una bandeja vacía casi siempre significa que la casilla no está conectada, y eso se arregla en Ajustes → Canales.',
      cuando: 'vacio',
    },
  ],
  leads: [
    {
      ancla: 'leads-nuevo',
      titulo: 'De dónde salen los prospectos',
      texto: 'El pipeline busca negocios de un rubro y una zona, los califica y prepara los borradores del primer correo. Si activaste el envío automático, los manda sin pasar por ti — conviene saberlo antes de prenderlo.',
    },
    {
      ancla: 'leads-etapa',
      titulo: 'La etapa dice en qué punto está cada uno',
      texto: 'Un prospecto avanza de etapa a medida que se lo contacta y responde. Es lo que separa «todavía no le escribí» de «está esperando respuesta mía».',
      cuando: 'con-datos',
    },
    {
      ancla: 'leads-filtros',
      titulo: 'Filtrar por lo que sirve para trabajar',
      texto: 'Con correo y sin correo, con sitio web y sin sitio. Sin correo no hay a quién escribirle; sin sitio hay algo concreto que ofrecerle. Los filtros están para armar el grupo sobre el que vas a actuar.',
      cuando: 'con-datos',
    },
    {
      ancla: 'leads-seleccion',
      titulo: 'Mover varios de etapa a la vez',
      texto: 'Marca los que quieras y la barra de arriba los mueve juntos o los borra. «Todos» marca lo que dejaste filtrado, no la lista entera.',
      cuando: 'con-datos',
    },
    {
      ancla: 'leads-fila',
      titulo: 'Un prospecto todavía no es un contacto',
      texto: 'Vive acá mientras es una posibilidad. Cuando responde y vale la pena, se convierte en contacto y recién ahí entra a tu base — así los cientos que no contestaron no te la ensucian.',
      cuando: 'con-datos',
    },
  ],
  projects: [
    {
      ancla: 'projects-nuevo',
      titulo: 'Un proyecto es el trabajo de un cliente',
      texto: 'Se le asigna un contacto y queda con su carpeta de archivos, su facturación y su sitio. Todo lo que pase con ese trabajo cuelga de acá en vez de estar suelto.',
    },
    {
      ancla: 'projects-col-estado',
      titulo: 'El estado dice en qué anda cada trabajo',
      texto: 'Es lo que mira Evo Brain cuando le preguntas qué tienes en curso, y lo que decide qué entra en el reporte que se le manda al cliente.',
      cuando: 'con-datos',
    },
    {
      ancla: 'projects-col-tipo',
      titulo: 'El tipo cambia lo que ves adentro',
      texto: 'Un sitio web trae diseño, publicación y mantenimiento; otros tipos de trabajo no los necesitan y no los muestran.',
      cuando: 'con-datos',
    },
    {
      ancla: 'projects-fila',
      titulo: 'Adentro está el trabajo de verdad',
      texto: 'Al abrir un proyecto están el brief, las páginas, la facturación y el mantenimiento del sitio: medir cómo está, arreglar lo que se puede, escribir artículos y mandarle el reporte al cliente.',
      cuando: 'con-datos',
    },
    {
      ancla: 'projects-toolbar',
      titulo: 'Filtrar por estado o por cliente',
      texto: 'Para ver solo lo que está activo, o todo lo de un mismo cliente junto.',
      cuando: 'con-datos',
      anchoMin: 900,
    },
  ],
  forms: [
    {
      ancla: 'forms-nuevo',
      titulo: 'Un formulario público, sin tener sitio',
      texto: 'Armas los campos acá y se publica con su propio enlace, listo para pegar donde quieras: en tu sitio, en una historia o en un mensaje.',
    },
    {
      ancla: 'forms-col-pendientes',
      titulo: 'Pendientes es lo que nadie miró',
      texto: 'Respuestas es el total que llegó; Pendientes es cuántas siguen sin revisar. Ese segundo número es el que aparece en el menú lateral y se apaga cuando las miras.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-col-estado',
      titulo: 'Deshabilitar no borra nada',
      texto: 'Un formulario deshabilitado deja de aceptar respuestas y conserva las que ya tiene. Y los que tienen respuestas no se pueden borrar: perderías lo que la gente contestó.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-fila',
      titulo: 'Lo que llega puede seguir solo',
      texto: 'En los ajustes del formulario eliges a qué embudo entra cada respuesta. Desde ese momento, cada una crea una carta en la primera etapa sin que hagas nada.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-seleccion',
      titulo: 'Abrir y cerrar varios a la vez',
      texto: 'Sirve cuando termina una campaña y hay que cerrar los formularios que quedaron abiertos.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-lista',
      titulo: 'Lo que llega puede seguir solo',
      texto: 'Al crear un formulario puedes conectarlo a un embudo: cada respuesta entra como una carta en la primera etapa, y de ahí sale el contacto y la cotización sin cargar nada a mano.',
      cuando: 'vacio',
    },
  ],
  funnels: [
    {
      ancla: 'funnels-nuevo',
      titulo: 'Empieza por crear un embudo',
      texto: 'Un embudo es un tablero con las etapas que tú defines. Con el botón de arriba creas el primero y eliges cómo se llama cada etapa.',
      cuando: 'vacio',
    },
    {
      ancla: 'funnels-lista',
      titulo: 'Para qué sirve un embudo',
      texto: 'Para ver en qué punto está cada oportunidad sin llevar la cuenta de memoria. Cada columna es una etapa, y la columna donde está una carta dice si sigue abierta, se ganó o se perdió.',
      cuando: 'vacio',
    },
    {
      ancla: 'funnels-lista',
      titulo: 'Y para no cargarlas a mano',
      texto: 'Si conectas un formulario al embudo, cada respuesta entra sola como una carta en la primera etapa. Se configura en Formularios, dentro de los ajustes del formulario.',
      cuando: 'vacio',
    },
    {
      ancla: 'funnels-fila',
      titulo: 'Un embudo es un tablero',
      texto: 'Ábrelo y vas a ver tus oportunidades en columnas. Mover una carta de columna es todo el trabajo: la columna donde cae define si sigue abierta, se ganó o se perdió.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-col-formulario',
      titulo: 'Las oportunidades entran solas',
      texto: 'Si conectas un formulario al embudo, cada respuesta crea una carta en la primera etapa. Se configura en Formularios, dentro de los ajustes del formulario.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-col-etapas',
      titulo: 'Las etapas las defines tú',
      texto: 'Cada embudo tiene las suyas. Desde el tablero, «Editar embudo» las renombra, las reordena y decide cuál significa ganado o perdido. Una etapa con cartas adentro no se puede borrar.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-col-valor',
      titulo: 'Cuánto hay en juego',
      texto: 'Abiertas son las oportunidades vivas; Valor es la suma de lo que valen. Las que se cierran, se ganen o se pierdan, dejan de contar en los dos números.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-nuevo',
      titulo: 'Un embudo por proceso',
      texto: 'Ventas, reservas, postulaciones: cada uno con sus propias etapas. Conviene separarlos en vez de meter todo en el mismo tablero, porque las etapas de uno no sirven para el otro.',
      cuando: 'con-datos',
    },
  ],
  contacts: [
    {
      ancla: 'contacts-nuevo',
      titulo: 'Dos formas de sumar gente',
      texto: 'Uno por uno con «Nuevo contacto», o de a muchos con «Importar CSV»: subes el archivo, eliges qué columna es cada cosa, y lo que ya existe no se duplica.',
    },
    {
      ancla: 'contacts-col-categoria',
      titulo: 'La categoría decide quién es quién',
      texto: 'Cliente, prospecto o personal. No es una etiqueta decorativa: es lo que Evo Brain usa cuando le pides «mis clientes», y lo que filtran los demás módulos. Las tuyas se crean en Ajustes → Datos.',
    },
    {
      ancla: 'contacts-lista',
      titulo: 'No hace falta cargarlos a mano',
      texto: 'Los contactos también entran solos: una respuesta de formulario o una oportunidad que ganas crean el contacto, y si ya existe con ese correo o teléfono lo reutilizan en vez de duplicarlo.',
      cuando: 'vacio',
    },
    {
      ancla: 'contacts-fila',
      titulo: 'Lo que hay adentro de una ficha',
      texto: 'Al abrir un contacto están sus datos, sus notas, sus etiquetas y todo lo que pasó con él. Desde ahí se le escribe, se le cotiza y se activa su perfil inteligente.',
      cuando: 'con-datos',
    },
    {
      ancla: 'contacts-toolbar',
      titulo: 'Filtrar para trabajar sobre un grupo',
      texto: 'Categoría, origen y etiqueta se combinan. El número al lado de cada categoría es cuántos hay. Lo que dejes filtrado es sobre lo que actúan las acciones en lote.',
      cuando: 'con-datos',
    },
    {
      ancla: 'contacts-seleccion',
      titulo: 'Trabajar de a muchos',
      texto: 'Marca varios y aparece una barra arriba: cambiar categoría, poner una etiqueta o borrar. «Todos» marca lo que estás viendo en ese momento, no toda tu base.',
      cuando: 'con-datos',
    },
    {
      ancla: 'contacts-col-origen',
      titulo: 'De dónde salió cada uno',
      texto: 'Correo, formulario, importación o carga manual. Sirve para saber si un contacto llegó solo o lo cargaste tú, y para filtrar por eso cuando quieras trabajar sobre un grupo.',
      cuando: 'con-datos',
    },
  ],
};

/**
 * El inglés.
 *
 * Estuvo vacío exactamente medio día, y ese medio día el recorrido no existió
 * para nadie que use la app en inglés — que es la mitad de los clientes y el
 * propio Eduardo. El razonamiento de entonces ("sin traducción no hay
 * recorrido, devolver el español sería ruido") no era falso; lo que estaba mal
 * era el efecto combinado con que un recorrido sin pasos se apaga en silencio.
 *
 * Los dos idiomas tienen los mismos módulos, y `recorridos.test.ts` rompe el
 * build si eso deja de ser cierto.
 */
const EN: Recorridos = {
  reports: [
    {
      ancla: 'reports-nuevo',
      titulo: 'Two kinds of report',
      texto: 'A web audit measures a site with Google PageSpeed — speed, SEO and accessibility — and an internal query publishes the answer to a question about your own data as a document.',
    },
    {
      ancla: 'reports-estado',
      titulo: 'A report is not ready instantly',
      texto: 'Pending, Generating, Ready or Failed. The worker builds it in the background: you can close the screen and do something else, it shows up here when it is done.',
      cuando: 'con-datos',
    },
    {
      ancla: 'reports-fila',
      titulo: 'You share it with a link',
      texto: 'A finished report goes to the client as a link that opens with no account and nothing to install. That is what makes it useful for showing work.',
      cuando: 'con-datos',
    },
    {
      ancla: 'reports-tipo',
      titulo: 'They pile up fast',
      texto: 'Every measurement of every site is kept. Filtering by type and status is what keeps this list usable after the first month.',
      cuando: 'con-datos',
      anchoMin: 900,
    },
    {
      ancla: 'reports-lista',
      titulo: 'What the first one is for',
      texto: 'A web audit on a client site gives you the picture of where it stands today: what is slow, what it is missing for Google and what barriers it has. It is what justifies the work that comes next.',
      cuando: 'vacio',
    },
  ],
  billing: [
    {
      ancla: 'billing-vista',
      titulo: 'Three different things in one place',
      texto: 'A quote is what you propose; an invoice is what you charge; a recurring is an automatic card charge: the client enters it once and gets billed every month on its own, with nothing to issue.',
    },
    {
      ancla: 'billing-nuevo',
      titulo: 'The contact is required, the project is not',
      texto: 'No contact, no document. No project is fine: the quote exists anyway and shows up on the contact and in this list — it just will not appear in any project billing tab.',
    },
    {
      ancla: 'billing-fila',
      titulo: 'Quote to invoice without redoing it',
      texto: 'What the client approves becomes an invoice with the same lines. And the items you always use are saved once in Settings → Data and pulled in from the search box.',
      cuando: 'con-datos',
    },
    {
      ancla: 'billing-buscar',
      titulo: 'Search by number or by client',
      texto: 'The number is the document reference. Useful when a client writes quoting it and you have no idea which one it was.',
      cuando: 'con-datos',
      anchoMin: 900,
    },
    {
      ancla: 'billing-lista',
      titulo: 'Sending always asks you first',
      texto: 'Evo Brain can build the whole document, lines and price included, but sending it to the client stops and waits for your go-ahead. It is money, and a sent document cannot be taken back.',
      cuando: 'vacio',
    },
  ],
  messaging: [
    {
      ancla: 'messaging-canal',
      titulo: 'Every channel in one inbox',
      texto: 'Instagram, Messenger and WhatsApp in the same list, without opening each app. You connect them once in Settings → Channels and from then on they arrive on their own.',
    },
    {
      ancla: 'messaging-estado',
      titulo: '"Expiring" is the one to watch',
      texto: 'Meta gives you 24 hours to reply, counted from the person last message. After that you cannot write to them until they write again. That is why letting one expire is expensive.',
      cuando: 'con-datos',
    },
    {
      ancla: 'messaging-fila',
      titulo: 'You reply here, it lands as always',
      texto: 'The person gets your reply in the same Instagram or WhatsApp conversation where they wrote you. They never know you answered from somewhere else.',
      cuando: 'con-datos',
    },
    {
      ancla: 'messaging-estado',
      titulo: 'Archiving is not deleting',
      texto: 'It takes the conversation out of the list and keeps the history. If the person writes again, it comes back with everything that was said before.',
      cuando: 'con-datos',
    },
    {
      ancla: 'messaging-lista',
      titulo: 'First you connect a channel',
      texto: 'Instagram, Messenger and WhatsApp messages land here once you connect the account in Settings → Channels. Until then this list stays empty even if people are writing you.',
      cuando: 'vacio',
    },
  ],
  tasks: [
    {
      ancla: 'tasks-lista',
      titulo: 'Anything that takes time lands here',
      texto: 'What you ask Evo Brain for, the analyses, the pages being generated and the reports. They do not happen while you wait: they become tasks and run in the background.',
    },
    {
      ancla: 'tasks-estado',
      titulo: 'Two statuses worth understanding',
      texto: '"Blocked" means something is missing before it can go on — a key, a piece of data, a decision from you. "Failed" means it was tried and went wrong. The other five explain themselves.',
      cuando: 'con-datos',
    },
    {
      ancla: 'tasks-fila',
      titulo: 'A task tells you what it did',
      texto: 'Open it and you see what actually happened, not just that it finished. And if it failed, why — which is what you look at when something came out different from what you expected.',
      cuando: 'con-datos',
    },
    {
      ancla: 'tasks-lista',
      titulo: 'Who decides if Evo Brain runs on its own',
      texto: 'Settings → Automation has a switch between "proposes and waits" and "runs on its own". On the first one, a task does not start until you approve it here. No need to touch it on day one, but worth knowing it is there.',
    },
    {
      ancla: 'tasks-nuevo',
      titulo: 'You can queue one yourself',
      texto: 'Without asking by chat: the task is created, sits in the list and runs like the ones Evo Brain queues.',
      cuando: 'con-datos',
    },
  ],
  email: [
    {
      ancla: 'email-vistas',
      titulo: 'Your inbox has seven views',
      texto: 'All, Unread, Follow-up, Sent, Drafts, Categories and Trash. The one you would not guess is Follow-up: it gathers what is still waiting on a reply from you, with the count next to it.',
    },
    {
      ancla: 'email-categorias',
      titulo: 'Mail arrives already sorted',
      texto: 'Every message lands in a category as it comes in. If you cannot find one, check here before giving it up for lost: the sidebar counter ignores anything sitting in a hidden category.',
    },
    {
      ancla: 'email-modo',
      titulo: 'Single message or conversation',
      texto: 'Threads groups everything back and forth with the same person on the same subject. Messages lists them one by one, as they arrived.',
      anchoMin: 900,
    },
    {
      ancla: 'email-fila',
      titulo: 'Replies go out from your own mailbox',
      texto: 'Evo Hub is not a new address: it reads from your usual mailbox and replies through it. Whoever gets your reply sees your address, not a platform one.',
      cuando: 'con-datos',
    },
    {
      ancla: 'email-lista',
      titulo: 'Empty inbox? Check the mailbox',
      texto: 'Evo Hub does not receive mail: it reads yours every few minutes. An empty inbox almost always means the mailbox is not connected, and that is fixed in Settings → Channels.',
      cuando: 'vacio',
    },
  ],
  leads: [
    {
      ancla: 'leads-nuevo',
      titulo: 'Where prospects come from',
      texto: 'The pipeline finds businesses in a trade and an area, qualifies them and drafts the first email. If you turned automatic sending on, it sends them without going through you — worth knowing before you turn it on.',
    },
    {
      ancla: 'leads-etapa',
      titulo: 'The stage says where each one stands',
      texto: 'A prospect moves through stages as you reach out and they answer. It is what separates "I have not written yet" from "they are waiting on me".',
      cuando: 'con-datos',
    },
    {
      ancla: 'leads-filtros',
      titulo: 'Filter by what helps you work',
      texto: 'With and without email, with and without a website. No email means nobody to write to; no website means something concrete to offer. The filters are there to build the group you are about to act on.',
      cuando: 'con-datos',
    },
    {
      ancla: 'leads-seleccion',
      titulo: 'Move several stages at once',
      texto: 'Select the ones you want and the bar above moves them together or deletes them. "All" selects what you left filtered, not the whole list.',
      cuando: 'con-datos',
    },
    {
      ancla: 'leads-fila',
      titulo: 'A prospect is not a contact yet',
      texto: 'They live here while they are still a maybe. When one replies and it is worth it, they become a contact and only then enter your database — so the hundreds who never answered do not clutter it.',
      cuando: 'con-datos',
    },
  ],
  projects: [
    {
      ancla: 'projects-nuevo',
      titulo: 'A project is one client job',
      texto: 'You assign it a contact and it gets its own file folder, its billing and its site. Everything about that job hangs from here instead of floating around.',
    },
    {
      ancla: 'projects-col-estado',
      titulo: 'Status says where each job stands',
      texto: 'It is what Evo Brain reads when you ask what you have in progress, and what decides what goes into the report sent to the client.',
      cuando: 'con-datos',
    },
    {
      ancla: 'projects-col-tipo',
      titulo: 'Type changes what you see inside',
      texto: 'A website brings design, publishing and maintenance; other kinds of work do not need them and do not show them.',
      cuando: 'con-datos',
    },
    {
      ancla: 'projects-fila',
      titulo: 'The actual work is inside',
      texto: 'Open a project and you get the brief, the pages, the billing and the site maintenance: measuring how it is doing, fixing what can be fixed, writing articles and sending the client their report.',
      cuando: 'con-datos',
    },
    {
      ancla: 'projects-toolbar',
      titulo: 'Filter by status or by client',
      texto: 'To see only what is active, or everything for one client together.',
      cuando: 'con-datos',
      anchoMin: 900,
    },
  ],
  forms: [
    {
      ancla: 'forms-nuevo',
      titulo: 'A public form without owning a site',
      texto: 'You build the fields here and it goes live with its own link, ready to paste anywhere: on your site, in a story or in a message.',
    },
    {
      ancla: 'forms-col-pendientes',
      titulo: 'Pending is what nobody has read',
      texto: 'Replies is everything that came in; Pending is how many are still unreviewed. That second number is the one in the sidebar, and it clears as you read them.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-col-estado',
      titulo: 'Disabling deletes nothing',
      texto: 'A disabled form stops accepting replies and keeps the ones it has. And forms with replies cannot be deleted: you would lose what people answered.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-fila',
      titulo: 'What arrives can keep going on its own',
      texto: 'In the form settings you pick which funnel each reply enters. From then on, every one creates a card in the first stage without you doing anything.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-seleccion',
      titulo: 'Open and close several at once',
      texto: 'Handy when a campaign ends and there are forms left open.',
      cuando: 'con-datos',
    },
    {
      ancla: 'forms-lista',
      titulo: 'What arrives can keep going on its own',
      texto: 'When you create a form you can connect it to a funnel: each reply enters as a card in the first stage, and from there the contact and the quote follow without typing anything.',
      cuando: 'vacio',
    },
  ],
  funnels: [
    {
      ancla: 'funnels-nuevo',
      titulo: 'Start by creating a funnel',
      texto: 'A funnel is a board with the stages you define. The button above creates your first one, and you choose what each stage is called.',
      cuando: 'vacio',
    },
    {
      ancla: 'funnels-lista',
      titulo: 'What a funnel is for',
      texto: 'To see where every opportunity stands without keeping track in your head. Each column is a stage, and the column a card sits in says whether it is still open, won or lost.',
      cuando: 'vacio',
    },
    {
      ancla: 'funnels-lista',
      titulo: 'And so you do not type them in',
      texto: 'Connect a form to the funnel and every reply arrives on its own as a card in the first stage. You set it up in Forms, inside that form settings.',
      cuando: 'vacio',
    },
    {
      ancla: 'funnels-fila',
      titulo: 'A funnel is a board',
      texto: 'Open it and you will see your opportunities in columns. Moving a card between columns is the whole job: the column it lands in defines whether it is open, won or lost.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-col-formulario',
      titulo: 'Opportunities arrive on their own',
      texto: 'Connect a form to the funnel and every reply creates a card in the first stage. You set it up in Forms, inside that form settings.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-col-etapas',
      titulo: 'You define the stages',
      texto: 'Each funnel has its own. From the board, "Edit funnel" renames them, reorders them and decides which one means won or lost. A stage with cards in it cannot be deleted.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-col-valor',
      titulo: 'What is at stake',
      texto: 'Open is how many opportunities are still alive; Value is what they add up to. Once one closes, won or lost, it stops counting in both numbers.',
      cuando: 'con-datos',
    },
    {
      ancla: 'funnels-nuevo',
      titulo: 'One funnel per process',
      texto: 'Sales, bookings, applications: each with its own stages. Keeping them apart beats putting everything on one board, because one process stages do not fit the other.',
      cuando: 'con-datos',
    },
  ],
  contacts: [
    {
      ancla: 'contacts-nuevo',
      titulo: 'Two ways to add people',
      texto: 'One at a time with "New contact", or in bulk with "Import CSV": upload the file, pick which column is what, and anything that already exists is not duplicated.',
    },
    {
      ancla: 'contacts-col-categoria',
      titulo: 'The category says who is who',
      texto: 'Client, prospect or personal. Not a decorative label: it is what Evo Brain uses when you ask about "my clients", and what the other modules filter by. Add your own in Settings → Data.',
    },
    {
      ancla: 'contacts-lista',
      titulo: 'You do not have to type them in',
      texto: 'Contacts also arrive on their own: a form reply or an opportunity you win creates the contact, and if one already exists with that email or phone it is reused instead of duplicated.',
      cuando: 'vacio',
    },
    {
      ancla: 'contacts-fila',
      titulo: 'What is inside a contact',
      texto: 'Open one and you get their details, their notes, their tags and everything that has happened with them. From there you write to them, quote them and turn on their smart profile.',
      cuando: 'con-datos',
    },
    {
      ancla: 'contacts-toolbar',
      titulo: 'Filter to work on a group',
      texto: 'Category, source and tag combine. The number next to each category is how many there are. Whatever you leave filtered is what the bulk actions will act on.',
      cuando: 'con-datos',
    },
    {
      ancla: 'contacts-seleccion',
      titulo: 'Working on many at once',
      texto: 'Select a few and a bar appears above: change category, add a tag or delete. "All" selects what you are looking at right now, not your whole database.',
      cuando: 'con-datos',
    },
    {
      ancla: 'contacts-col-origen',
      titulo: 'Where each one came from',
      texto: 'Email, form, import or typed in by hand. It tells you whether a contact arrived on its own or you added them, and you can filter by it when working on a group.',
      cuando: 'con-datos',
    },
  ],
};

export function cargarRecorrido(modulo: ModuloConRecorrido, idioma: IdiomaAyuda): PasoDeRecorrido[] {
  const fuente = idioma === 'es' ? ES : EN;
  return fuente[modulo] ?? [];
}

/** Los módulos que hoy tienen recorrido en ese idioma. */
export function modulosConRecorrido(idioma: IdiomaAyuda): ModuloConRecorrido[] {
  const fuente = idioma === 'es' ? ES : EN;
  return (Object.keys(fuente) as ModuloConRecorrido[]).filter(m => (fuente[m] ?? []).length > 0);
}

/**
 * El artículo de ayuda de un módulo, si hay uno.
 *
 * Lo usa el último paso del recorrido para ofrecer «leer más». Sale de los
 * artículos mismos —su campo `modulo`— y no de una lista aparte: una tercera
 * lista que mantener a mano es una que se desactualiza.
 */
export function articuloDelModulo(
  modulo: ModuloConRecorrido,
  articulos: Array<{ id: string; titulo: string; modulo: string }>,
): { id: string; titulo: string } | null {
  const a = articulos.find(x => x.modulo === modulo);
  return a ? { id: a.id, titulo: a.titulo } : null;
}

/** Todas las anclas declaradas, para el test que verifica que existan. */
export function anclasDeclaradas(): Array<{ modulo: ModuloConRecorrido; ancla: string }> {
  return (Object.keys(ES) as ModuloConRecorrido[])
    .flatMap(m => ES[m].map(p => ({ modulo: m, ancla: p.ancla })));
}
